import React, { createContext, useContext, useMemo } from 'react';
import { useFilterActions } from './ActionsProvider';
import { useFilterState } from './StateProvider';
import { fromSummaryDiffToChanges, useDiff } from '../../../Shared/summaryDiff';
import { useTransportTypeProduct } from '@components/Filters/Components/TransportTypeFilter/Provider';

export type AirportType = {
  checked: boolean;
  disabled: boolean;
  cityIata: string;
  cityName: string;
  countryName: string;
  iata: string;
  id: string;
  locationType: string;
  name: string;
};

export type SummaryType = {
  type: string;
  isApplied: boolean;
  isUnsuited: boolean;
  summary: AirportType[];
};

type ChangesType = {
  tag: {
    legId: number;
    alias: string;
    type: string;
    undo: () => void;
  };
  type: string;
};

const AppliedAirportFilterContext = createContext(false);
const UnsuitedAirportFilterContext = createContext(false);
const AirportFilterSummaryContext = createContext<SummaryType[][]>(
  [] as SummaryType[][]
);
const FilterChangesContext = createContext<ChangesType[]>([] as ChangesType[]);

const noAirportFilterSummary = {
  summary: [],
  isApplied: false,
  isUnsuited: false,
};

const createFilterSummary = ({ airports }, product) => {
  if (!airports || airports.length === 0) {
    return noAirportFilterSummary;
  }
  const calculateSummaryAirports = (legAirports, product) => {
    if (!legAirports) {
      return noAirportFilterSummary;
    }
    const type = product === 'TRAIN' ? 'TRAIN_STATION' : 'AIRPORT';
    const legAirportsWithType =
      product !== ''
        ? legAirports.filter(({ locationType }) => locationType === type)
        : legAirports;
    const checkedAirports = legAirports.filter(({ checked }) => checked);
    const checkedAirportsWithType =
      product !== ''
        ? checkedAirports.filter(({ locationType }) => locationType === type)
        : checkedAirports;
    const isApplied =
      checkedAirportsWithType.length < legAirportsWithType.length;
    const isUnsuited = checkedAirportsWithType.length === 0;
    return {
      summary: !isApplied ? [] : checkedAirportsWithType,
      isApplied,
      isUnsuited,
    };
  };

  const summary = [] as SummaryType[][];
  let isUnsuited = false;
  let isApplied = false;
  airports.forEach(({ departureAirports, destinationAirports }) => {
    const departureSummary = calculateSummaryAirports(
      departureAirports,
      product
    );
    const destinationSummary = calculateSummaryAirports(
      destinationAirports,
      product
    );

    isUnsuited =
      isUnsuited ||
      departureSummary.isUnsuited ||
      destinationSummary.isUnsuited;
    isApplied =
      isApplied || departureSummary.isApplied || destinationSummary.isApplied;

    const summaryAirports = [
      {
        type: 'departure',
        ...departureSummary,
      },
      {
        type: 'destination',
        ...destinationSummary,
      },
    ];
    if (summaryAirports.length > 0) {
      summary.push(summaryAirports);
    }
  });

  return {
    summary,
    isApplied,
    isUnsuited,
  };
};

const normalizeDepartures = (summary) =>
  summary && summary.map(([departure]) => departure);
const normalizeDestinations = (summary) =>
  summary && summary.map(([, destination]) => destination);

const normalizeAirportsDiff = (previous, next) => {
  return {
    departureAirports: {
      previous: normalizeDepartures(previous),
      next: normalizeDepartures(next),
    },
    destinationAirports: {
      previous: normalizeDestinations(previous),
      next: normalizeDestinations(next),
    },
  };
};

const FilterChangesProvider = ({ summary, children }) => {
  const { previous, next } = useDiff(summary);
  const { departureAirports, destinationAirports } = normalizeAirportsDiff(
    previous,
    next
  );
  const { selectLegAll: resetLegAirports } = useFilterActions();
  const reset = useMemo(() => {
    const createReset =
      (airportType) =>
      ({ legId }) =>
        resetLegAirports({
          airportType,
          legId,
        });
    return {
      departureAirports: createReset('departureAirports'),
      destinationAirports: createReset('destinationAirports'),
    };
  }, [resetLegAirports]);
  const changes = [
    ...fromSummaryDiffToChanges(
      departureAirports.previous,
      departureAirports.next,
      'departureAirports',
      reset.departureAirports
    ),
    ...fromSummaryDiffToChanges(
      destinationAirports.previous,
      destinationAirports.next,
      'destinationAirports',
      reset.destinationAirports
    ),
  ];
  return (
    <FilterChangesContext.Provider value={changes}>
      {children}
    </FilterChangesContext.Provider>
  );
};

const AirportFilterSummaryProvider = ({ children }) => {
  const airports = useFilterState();
  const { product } = useTransportTypeProduct();
  const { summary, isApplied, isUnsuited } = createFilterSummary(
    airports,
    product
  );
  return (
    <AirportFilterSummaryContext.Provider value={summary}>
      <FilterChangesProvider summary={summary}>
        <AppliedAirportFilterContext.Provider value={isApplied}>
          <UnsuitedAirportFilterContext.Provider value={isUnsuited}>
            {children}
          </UnsuitedAirportFilterContext.Provider>
        </AppliedAirportFilterContext.Provider>
      </FilterChangesProvider>
    </AirportFilterSummaryContext.Provider>
  );
};

export const useFilterSummary = () => useContext(AirportFilterSummaryContext);
export const useFilterChanges = () => useContext(FilterChangesContext);
export const useAppliedFilter = () => useContext(AppliedAirportFilterContext);
export const useUnsuitedFilter = () => useContext(UnsuitedAirportFilterContext);

export default AirportFilterSummaryProvider;
